import { Button, CircularProgress, IconButton, Paper, TextField } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HistoryIcon from '@material-ui/icons/History';
import SearchIcon from '@material-ui/icons/Search';
import clsx from 'clsx';
import React, { useMemo, useRef, useState } from 'react';
import { SEARCH_HISTORY_MAX, SEARCH_HISTORY_SHOW } from '../constants';
import useStyles from '../styles';
import { loadSearchHistory, saveSearchHistory } from '../utils';

// Tô đậm phần CHƯA gõ: "29A" + "12345" -> 29A<b>12345</b>
const renderHighlight = (text, keyword) => {
  if (!keyword) return text;
  const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (idx < 0) return <b>{text}</b>;
  return (
    <>
      {idx > 0 && <b>{text.slice(0, idx)}</b>}
      {text.slice(idx, idx + keyword.length)}
      <b>{text.slice(idx + keyword.length)}</b>
    </>
  );
};

const SearchBar = ({ value, onChange, onSearch, searching }) => {
  const classes = useStyles();
  const [history, setHistory] = useState(loadSearchHistory);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchedValue, setSearchedValue] = useState('');
  const inputRef = useRef(null);

  // Chưa sửa gì kể từ lần tra cứu gần nhất -> coi như chưa gõ, hiện toàn bộ lịch sử 
  const keyword = value.trim() === searchedValue ? '' : value.trim();
  const suggestions = useMemo(() => {
    const list = keyword
      ? history.filter(item => item.toLowerCase().includes(keyword.toLowerCase()) && item !== keyword)
      : history;
    return list.slice(0, SEARCH_HISTORY_SHOW);
  }, [history, keyword]);

  const showDropdown = focused && suggestions.length > 0;

  const updateHistory = next => {
    setHistory(next);
    saveSearchHistory(next);
  };

  const submit = text => {
    const q = (typeof text === 'string' ? text : value).trim();
    if (q) {
      updateHistory([q, ...history.filter(item => item !== q)].slice(0, SEARCH_HISTORY_MAX));
    }
    setSearchedValue(q);
    setActiveIndex(-1);
    setFocused(false);
    onSearch(q);
  };

  const selectSuggestion = item => {
    onChange(item);
    submit(item);
  };

  const removeItem = (e, item) => {
    e.stopPropagation();
    updateHistory(history.filter(h => h !== item));
    setActiveIndex(-1);
  };

  // Xóa nhanh ô search, giữ focus để dropdown hiện lại toàn bộ lịch sử 
  const clearInput = () => {
    onChange('');
    setActiveIndex(-1);
    setFocused(true);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = e => {
    if (e.key === 'ArrowDown' && showDropdown) {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp' && showDropdown) {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setFocused(false);
      setActiveIndex(-1);
    } else if (e.key === 'Enter') {
      if (showDropdown && activeIndex >= 0) {
        selectSuggestion(suggestions[activeIndex]);
      } else {
        submit();
      }
    }
  };

  return (
    <div className={classes.searchWrapper}>
      <Paper className={clsx(classes.searchBar, showDropdown && classes.searchBarOpen)} elevation={0}>
        <SearchIcon className={classes.searchIcon} />
        <TextField
          fullWidth
          variant="standard"
          placeholder="Nhập biển số xe, serial thiết bị hoặc số điện thoại..."
          value={value}
          onChange={e => {
            onChange(e.target.value);
            setActiveIndex(-1);
            setFocused(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setActiveIndex(-1);
          }}
          onClick={() => setFocused(true)}
          inputRef={inputRef}
          inputProps={{ autoComplete: 'off' }}
          InputProps={{ disableUnderline: true, className: classes.searchInput }}
        />
        {value && (
          <IconButton
            size="small"
            className={classes.searchClear}
            title="Xóa nội dung tìm kiếm"
            onMouseDown={e => e.preventDefault()}
            onClick={clearInput}
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Button
          className={classes.searchButton}
          color="primary"
          variant="contained"
          onClick={() => submit()}
          disabled={searching}
          startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon style={{ fontSize: 18 }} />}
        >
          Tra cứu
        </Button>
      </Paper>

      {showDropdown && (
        // preventDefault ở mousedown để input không bị blur trước khi click kịp chạy
        <Paper className={classes.historyDropdown} elevation={0} onMouseDown={e => e.preventDefault()}>
          <div className={classes.historyHeader}>
            <span>Tìm kiếm gần đây</span>
            <span className={classes.historyClearAll} onClick={() => updateHistory([])}>
              Xóa tất cả
            </span>
          </div>
          {suggestions.map((item, index) => (
            <div
              key={item}
              className={clsx(classes.historyItem, index === activeIndex && classes.historyItemActive)}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectSuggestion(item)}
            >
              <HistoryIcon className={classes.historyIcon} />
              <span className={classes.historyText}>{renderHighlight(item, keyword)}</span>
              <IconButton
                size="small"
                className={classes.historyRemove}
                title="Xóa khỏi lịch sử"
                onClick={e => removeItem(e, item)}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          ))}
        </Paper>
      )}
    </div>
  );
};

export default SearchBar;
